import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../Academy/AcademyList.css';
import { useUser } from '../context/UserContext';
import { hasPrivilege } from '../utils/hasPrivilege';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';

const AcademyList = () => {
  const { userData } = useUser();
  const { selectedCenter, setSelectedCenter } = useUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [academies, setAcademies] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const token = localStorage.getItem('auth-token');
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setrowsPerPage] = useState(10);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;

  useEffect(() => {
    setLoading(true);
    setError(null);
    const headers = {
      'Authorization': `Bearer ${token}`,
    };

    axios.get(process.env.REACT_APP_API_ACADEMY_LIST, { headers })
      .then((response) => {
        if (response.data.success) {
          const academyData = response.data.data.academies.map((academy) => ({
            id: academy?.id,
            name: academy?.name,
            email: academy?.user?.email,
            password: academy?.user?.otp,
            about: academy?.about,
            mobileNumber: academy?.user?.mobile_number,
            address: academy?.address,
            sport: academy?.sport || "",
            facilities: academy?.facilities?.map(facility => facility.name),
            locality: academy?.locality || "",
            coverImage: academy?.coverPic,
            notification: academy?.isNotificationEnabled,
            maxActiveMembership: academy?.maxActiveMembership || 0,
          }));

          setAcademies(academyData);
        } else {
          setError(response.data.message || "Unknown error");
        }
      })
      .catch((error) => {
        setError("There was an error fetching the academies: " + (error.message || error));
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const refreshData = () => {
    setLoading(true);
    setError(null);

    const headers = {
      'Authorization': `Bearer ${token}`,
    };

    axios.get(process.env.REACT_APP_API_ACADEMY_LIST, { headers })
      .then((response) => {
        if (response.data.success) {
          const academyData = response.data.data.academies.map((academy) => ({
            id: academy?.id,
            name: academy?.name,
            email: academy?.user?.email,
            password: academy?.user?.otp,
            about: academy?.about,
            mobileNumber: academy?.user?.mobile_number,
            address: academy?.address,
            sport: academy?.sport || "",
            facilities: academy?.facilities?.map(facility => facility.name),
            locality: academy?.locality || "",
            coverImage: academy?.coverPic,
            notification: academy?.isNotificationEnabled,
            maxActiveMembership: academy?.maxActiveMembership || 0,
          }));

          setAcademies(academyData);
        } else {
          setError(response.data.message || "Unknown error");
        }
      })
      .catch((error) => {
        setError("There was an error fetching the academies: " + (error.message || error));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleItemsPerPageChange = (event) => {
    setrowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen !== null && !event.target.closest('.dropdown-container')) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [dropdownOpen]);

  const filteredAndSortedAcademies = academies.filter((academy) => {
      const notificationStatus = academy.notification ? 'Yes' : 'No';
      return (
        Object.values(academy)
          .join(' ')
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        notificationStatus.toLowerCase().includes(searchTerm.toLowerCase())
      );}).sort((a, b) => {
      if (!sortConfig.key) return 0;

      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (typeof aValue === 'boolean') aValue = aValue ? 'yes' : 'no';
      if (typeof bValue === 'boolean') bValue = bValue ? 'yes' : 'no';

      if (Array.isArray(aValue)) aValue = aValue.join(', ');
      if (Array.isArray(bValue)) bValue = bValue.join(', ');

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aStr = aValue ? aValue.toString().toLowerCase() : '';
      const bStr = bValue ? bValue.toString().toLowerCase() : '';

      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

  const currentRows = filteredAndSortedAcademies.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredAndSortedAcademies.length / rowsPerPage);
  const totalEntries = filteredAndSortedAcademies.length;
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;

  const toggleDropdown = (academyId, e) => {
    e.stopPropagation();
    if (dropdownOpen === academyId) {
      return setDropdownOpen(null);
    }
    const { bottom, left } = e.currentTarget.getBoundingClientRect();
    setDropdownPos({
      top: bottom + window.scrollY,
      left: left + window.scrollX,
    });
    setDropdownOpen(academyId);
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prevPage) => prevPage - 1);
  };

  const handleDeleteAcademy = (academyId) => {
    const selectedAcademy = academies.find(academy => academy.id === academyId);

    Swal.fire({
      title: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        confirmDelete(selectedAcademy);
      }
    });
  };

  const confirmDelete = async (selectedAcademy) => {
    try {
      const deleteUrl = `${process.env.REACT_APP_API_ACADEMY_DELETE}/${selectedAcademy.id}`;

      const response = await fetch(deleteUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error('Failed to delete academy');
      }

      const updatedAcademies = academies.filter(academy => academy.id !== selectedAcademy.id);
      setAcademies(updatedAcademies);
      Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: 'The academy has been deleted.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    } catch (error) {

      Swal.fire({
        title: 'Error!',
        text: 'There was an error deleting the academy.',
        icon: 'error',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  const handleCreateAcademy = (event) => {
    const url = `/newAcademy`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };
  
  const handleActionClick = (event, academyId, action) => {
    const selectedAcademy = academies.find(academy => academy.id === academyId);

    switch (action) {
      case 'edit': {
        const url = `/newAcademy?academyId=${academyId}`;
        if (event.metaKey || event.ctrlKey) {
          window.open(url, "_blank");
        } else {
          navigate(url, { state: { academy: selectedAcademy } });
        }
        break;
      }
      case 'disable':
        handleDeleteAcademy(academyId);
        break;

      case 'player_list': {
        const url = `/academyPlayerList?academyId=${academyId}`;
        if (event.metaKey || event.ctrlKey) {
          window.open(url, "_blank");
        } else {
          navigate(url, { state: { academy: selectedAcademy } });
        }
        break;
      }
      case 'coach_list': {
        const url = `/academyCoachList?academyId=${academyId}`;
        if (event.metaKey || event.ctrlKey) {
          window.open(url, "_blank");
        } else {
          navigate(url, { state: { academy: selectedAcademy } });
        }
        break;
      }
      default:
        break;
    }
  };

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () => {
    return generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">
          {page}
        </span>
      )
    );
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleAcademyClick = (academy) => {
    navigate(`/batchList`, { state: { academyId: academy.id } });
    setSelectedCenter(academy.id);
    window.location.reload();
  };

  useEffect(() => {
    const closeOnScroll = () => setDropdownOpen(null);

    window.addEventListener('scroll', closeOnScroll, true);
    return () => window.removeEventListener('scroll', closeOnScroll, true);
  }, []);

  return (
    <div className="enrollment-container">

      <div className="search-bar">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? "has-value" : ""}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">
            🔍 Search
          </label>
        </div>
        <div>
          {hasPrivilege(userData, 'ACADEMY', 'ACADEMY_LIST_MODIFY') && (
            <button
              className="create-button-new"
              onClick={handleCreateAcademy}
            >
              <span className="create-button__icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="23"
                  height="23"
                  className="create-button__svg"
                >
                  <path
                    d="M12 5v14M5 12h14"
                    stroke="white"
                    strokeWidth="2.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="create-button__label">
                Create Academy
              </span>
            </button>
          )}
        </div>
      </div>

      <div className="items-and-dues">
        <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
          <option value={100}>100 items per page</option>
        </select>
        <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() =>
                    handleItemsPerPageChange({ target: { value: option } })
                  }
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <button className="tt-action-btn" onClick={refreshData}>
          <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading && (
        <div className="premium-loader">
          <div className="progress-track">
            <div className="progress-bar"></div>
          </div>
          <div className="status-text">
            <span>L</span>
            <span>O</span>
            <span>A</span>
            <span>D</span>
            <span>I</span>
            <span>N</span>
            <span>G</span>
          </div>
        </div>
      )}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && filteredAndSortedAcademies.length === 0 && (
        <div className="error-message">No data available</div>
      )}

      <div className="payment-list-scroll-container" style={{ overflowY: 'auto' }}>
        <table className="payment-list-table">
          <thead>
            <tr>
              {[
                { key: 'name', label: 'Name' },
                { key: 'about', label: 'About' },
                { key: 'address', label: 'Address' },
                { key: 'notification', label: 'Notification' }
              ].map(({ key, label }) => (
                <th key={key} className="challenges-column" onClick={() => handleSort(key)}>
                  <div className="th-content">
                    <span>{label}</span>
                    <FiChevronDown
                      className={`sort-icon ${sortConfig.key === key ? 'active' : ''
                        } ${sortConfig.key === key && sortConfig.direction === 'descending' ? 'desc' : ''}`}
                    />
                  </div>
                </th>
              ))}
              {hasPrivilege(userData, 'ACADEMY', 'ACADEMY_LIST_MODIFY') && <th>Action</th>}
            </tr>
          </thead>
          <tbody>
            {currentRows.map((academy) => (
              <tr key={academy.id}>
                <td>
                  <span
                    onClick={() => handleAcademyClick(academy)}
                    style={{ cursor: 'pointer', color: 'blue', textDecoration: 'none' }}
                  >
                    {academy.name}
                  </span>
                </td>
                <td>{academy.about}</td>
                <td>{academy.address}</td>
                <td>{academy.notification ? 'Yes' : 'No'}</td>
                {hasPrivilege(userData, 'ACADEMY', 'ACADEMY_LIST_MODIFY') && (
                  <td className="sport-actions-column">
                    <button
                      className="ellipsis-button"
                      onClick={(e) => toggleDropdown(academy.id, e)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '24px',
                      }}
                    >
                      &#x22EE;
                    </button>

                    {dropdownOpen === academy.id && (
                      <div
                        className="custom-dropdown-player"
                        style={{
                          position: 'absolute',
                          top: dropdownPos.top,
                          left: dropdownPos.left,
                          zIndex: 9999,
                          backgroundColor: 'white',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                          minWidth: '150px',
                        }}
                      >
                        <button
                          className="dropdown-item"
                          onClick={(e) => handleActionClick(e, academy.id, 'player_list')}
                        >
                          Player List
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={(e) => handleActionClick(e, academy.id, 'coach_list')}
                        >
                          Coach List
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={(e) => handleActionClick(e, academy.id, 'edit')}
                        >
                          Edit
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={(e) => handleActionClick(e, academy.id, 'disable')}
                        >
                          Disable
                        </button>

                      </div>
                    )}
                  </td>
                )}

              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button
          className="prev-button"
          onClick={handlePrevPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {renderPagination()}
        <button
          className="next-button"
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>

    </div>
  );
};

export default AcademyList;
